import { DateFormatType, formatDate } from "~/common/utils/DateFormatter";
import {
  type LocalizedTextDescriptor,
  type ProductDescriptorSelection,
  type ProductLineFlagsViewModel,
  type ProductLineFormattedViewModel,
  type ProductLineKitViewModel,
  type ProductLineMessageViewModel,
  type ProductLinePricingViewModel,
  type ProductLineSource,
  type ProductLineSnapshotViewModel,
  type ProductSubLineSnapshotViewModel,
  type ProductLineValidationViewModel,
  type ProductLineViewModel,
  type ProductTableMode,
} from "~/features/cart/cartV2/models/ProductTableViewModel";
import {
  displayBetterDeal,
  getPackageByCode,
  getPriceInfo,
  getSurchargeValueFromTotalPrice,
  getTotalPrice,
  isProductPMA,
  productHasInStock,
} from "~/domains/product/utils/productDescriptorUtils";
import { getTotalPriceForLineItem } from "~/features/cart/cart/utils/lineItemPriceUtils";
import { isValidQuantity } from "~/utils/packageEntryUtils";
import { isMinOrderQuantityRestrictionEnabled, isTariffSurchargeEnabled } from "~/features/cart/cart/utils/cartUtils";
import { canUseCommentsForProduct } from "~/utils/productUtils";
import { isKITProduct, type KITProductData } from "~/domains/product/types/ProductData";
import { CART_QTY_EXCEEDS_SALES_ERROR_KEY } from "~/domains/salesCampaign/constants";
import { SalesHelper } from "~/domains/salesCampaign/utils/salesHelper";
import { NumberFormatter } from "~/common/utils/NumberFormatter";
import { PackageEntrySource, type LineItem, isKitGroup } from "~/features/cart/cart/models/CartData";
import type { EntryGroup, KitGroup } from "~/features/cart/cart/dto/CartResponseDTO";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { buildLineRowId } from "~/features/cart/cartV2/utils/buildTableId";
import * as PriceUtils from "~/utils/PriceUtils";

type KitOptionalPart = LineItem & { parentLineItemId: number; optionalPartCode: string };

export interface BuildProductLineViewModelInput {
  mode: ProductTableMode;
  lineItem: LineItem;
  descriptor: ProductDescriptor | null;
  descriptorSelection: ProductDescriptorSelection;
  subLineItems?: KitOptionalPart[];
  allowPriceReferenceEditing?: boolean;
  optionalPartDescriptors?: Record<number, ProductDescriptor>;
  phaseoutLocked?: boolean;
}

export function buildKitSubLineItems(
  lineItem: LineItem,
  lineItemsByGroupNumber: Record<number, LineItem[]>,
  entryGroupsByNumber: Dict<EntryGroup>,
): KitOptionalPart[] {
  const kitGroupNumber = lineItem.groupNumbers.find((n) => {
    const group = entryGroupsByNumber[n];
    return group !== undefined && isKitGroup(group);
  });
  if (kitGroupNumber === undefined) {
    return [];
  }

  const kitGroup = entryGroupsByNumber[kitGroupNumber] as KitGroup;
  const isHousing = lineItem.pk === kitGroup.housingEntryPk;
  if (!isHousing) {
    return [];
  }

  return (lineItemsByGroupNumber[kitGroupNumber] ?? [])
    .filter((e) => e.pk !== kitGroup.housingEntryPk)
    .map(
      (e): KitOptionalPart => ({
        ...e,
        parentLineItemId: lineItem.id,
        optionalPartCode: e.productCode,
      }),
    );
}

const PHASE_IN_BUY_ERROR_KEY = "page.pdp.phaseinout.purchase.error";
const PHASEOUT2_EXCEED_STOCK_KEY = PHASE_IN_BUY_ERROR_KEY;

function findMinOrderQuantity(product: ProductDescriptor, packCode: string): number {
  return getPackageByCode(product, packCode)?.minOrderQuantity ?? 0;
}

function getDefaultQuantity(packSize: number, minOrderQuantity: number): number {
  if (minOrderQuantity < packSize) {
    return packSize > 1 ? packSize : 1;
  }

  if (minOrderQuantity % packSize === 0) {
    return minOrderQuantity;
  }

  return minOrderQuantity + (packSize - (minOrderQuantity % packSize));
}

function getDiscountFormatted(
  regularPrice: ProductLinePricingViewModel["displayPrice"] | undefined,
  salesPrice: ProductLinePricingViewModel["displayPrice"] | undefined,
) {
  const regularPriceIsValid = !!regularPrice && regularPrice.value > 0;
  const salesPriceIsValid = !!salesPrice && salesPrice.value > 0;
  const salesPriceIsBetter = salesPriceIsValid && (!regularPriceIsValid || salesPrice.value < regularPrice.value);

  if (!regularPriceIsValid || !salesPriceIsBetter) {
    return null;
  }

  const diff = regularPrice.value - salesPrice.value;
  const discountPercent = Math.round((diff / regularPrice.value) * 100);

  return `-${NumberFormatter.getWeightFormatterG().format(discountPercent)}%`;
}

function processSalesPricing(args: {
  netPrice: NonNullable<LineItem["netPrice"]> | null;
  salesPrice: LineItem["actualSalePrice"] | null;
  saleActivated: boolean;
}) {
  const { netPrice, salesPrice, saleActivated } = args;

  if (!saleActivated) {
    return {
      price: netPrice,
      oldPrice: null,
      netPrice,
      salesPrice,
      salesPriceIsBetter: false,
      discountFormatted: null,
    };
  }

  const regularPriceIsValid = !!netPrice && netPrice.value > 0;
  const salesPriceIsValid = !!salesPrice && salesPrice.value > 0;
  const salesPriceIsBetter = salesPriceIsValid && (!regularPriceIsValid || salesPrice.value < netPrice.value);

  return {
    price: salesPriceIsBetter ? salesPrice : netPrice,
    oldPrice: salesPriceIsBetter && regularPriceIsValid ? netPrice : null,
    netPrice,
    salesPrice,
    salesPriceIsBetter,
    discountFormatted: getDiscountFormatted(netPrice, salesPrice),
  };
}

function isDescriptorSpecialPrice(descriptor: ProductDescriptor | null, packCode: string): boolean {
  if (!descriptor) {
    return false;
  }

  return getPriceInfo({ product: descriptor, packCode })?.isSpecialPrice ?? false;
}

function toProductLineSource(source: LineItem["source"]): ProductLineSource {
  switch (source) {
    case PackageEntrySource.BULK:
      return "BULK";
    case PackageEntrySource.QUOTE:
      return "QUOTE";
    case PackageEntrySource.CONTRACT:
      return "CONTRACT";
    case PackageEntrySource.DEFAULT:
    default:
      return "DEFAULT";
  }
}

function buildLineSnapshot(lineItem: LineItem): ProductLineSnapshotViewModel {
  return {
    lineItemId: lineItem.id,
    pk: lineItem.pk,
    cartEntryId: lineItem.pk,
    productCode: lineItem.productCode,
    quantity: lineItem.quantity,
    packSize: lineItem.packSize ?? null,
    packCode: lineItem.packCode,
    deliveryDate: lineItem.deliveryDate,
    manuallySetRDD: lineItem.manuallySetRDD,
    comment: lineItem.comment ?? "",
    internalComment: lineItem.internalComment,
    valid: lineItem.valid,
    disableEdit: lineItem.disableEdit,
    primaryWarehouseName: lineItem.primaryWarehouseName,
    showComments: lineItem.showComments,
    priceReference: lineItem.priceReference,
    source: toProductLineSource(lineItem.source),
    quoteNumber: lineItem.quoteNumber,
    quoteLine: lineItem.quoteLine,
    contractNumber: lineItem.contractNumber,
    contractLine: lineItem.contractLine,
    estimatedShippingDate: lineItem.estimatedShippingDate,
    logisticalComment: lineItem.logisticalComment,
    edd: lineItem.edd,
    isInStock: lineItem.isInStock,
    minimumQty: lineItem.minimumQty,
    isBundled: lineItem.isBundled ?? false,
    original: lineItem.original,
    restrictedPackage: lineItem.restrictedPackage,
    isPackagingSelectionOpen: lineItem.isPackagingSelectionOpen,
    customerOrderNumber: lineItem.customerOrderNumber,
    partialDeliveries: lineItem.partialDeliveries,
    saleCampaignEnabled: lineItem.saleCampaignEnabled ?? false,
    netPrice: lineItem.netPrice,
  };
}

function buildSubLineSnapshot(subLineItem: KitOptionalPart): ProductSubLineSnapshotViewModel {
  return {
    ...buildLineSnapshot(subLineItem),
    parentLineItemId: subLineItem.parentLineItemId,
    optionalPartCode: subLineItem.optionalPartCode,
  };
}

const makeQuantityNotMetError = (packSize: number) => ({
  valid: false,
  label: {
    key: "page.moq.helperText.quantotyNotMet",
    params: { packSize },
    fallback: `Quantity must be a multiple of ${packSize}`,
  },
});

const makeMoQNotMetError = (minOrderQuantity: number) => ({
  valid: false,
  label: {
    key: "page.moq.helperText.moqNotMet",
    params: { minOrderQuantity },
    fallback: `Minimum quantity is ${minOrderQuantity}`,
  },
});

const makeMoQMetHelperText = (minOrderQuantity: number) => ({
  valid: true,
  label: {
    key: "page.moq.helperText.moqNotMet",
    params: { minOrderQuantity },
    fallback: `Minimum quantity is ${minOrderQuantity}`,
  },
});

const makeBothNotSatisfiedError = (packSize: number, minOrderQuantity: number) => ({
  valid: false,
  label: {
    key: "page.moq.helperText.bothNotSatisfied",
    params: { packSize, minOrderQuantity },
    fallback: `Quantity must be a multiple of ${packSize} and at least ${minOrderQuantity}`,
  },
});

function buildMOQHelperText(args: {
  quantity: number;
  packSize: number;
  minOrderQuantity: number;
  isAvailabilityLoaded: boolean;
  isPackageSelected: boolean;
  isOnSale?: boolean;
}): { valid: boolean; label: LocalizedTextDescriptor } | null {
  const { quantity, packSize, minOrderQuantity, isAvailabilityLoaded, isPackageSelected, isOnSale } = args;

  if (!isAvailabilityLoaded || !isPackageSelected) {
    return null;
  }

  let packSizeError = false;
  let minQuantityError = false;
  let minQuantityInfo = false;

  if (!Number.isInteger(quantity / packSize)) {
    packSizeError = true;
  }

  if (isMinOrderQuantityRestrictionEnabled() && !isOnSale) {
    if (minOrderQuantity > 1) {
      if (quantity < minOrderQuantity) {
        minQuantityError = true;
      } else {
        minQuantityInfo = true;
      }
    }
  } else {
    if (quantity === 0) {
      packSizeError = true;
    }
  }

  if (minQuantityInfo) {
    return makeMoQMetHelperText(packSize);
  }

  if (packSizeError && minQuantityError) {
    return makeBothNotSatisfiedError(packSize, minOrderQuantity);
  }

  if (packSizeError) {
    return makeQuantityNotMetError(packSize);
  }

  if (minQuantityError) {
    return makeMoQNotMetError(minOrderQuantity);
  }

  return null;
}

function buildLineMessages(lineItem: LineItem, descriptor: ProductDescriptor | null): ProductLineMessageViewModel[] {
  let messages = lineItem.messages.slice() as ProductLineMessageViewModel[];

  if (descriptor && displayBetterDeal({ product: descriptor, packCode: lineItem.packCode }, lineItem.quantity)) {
    messages.push({
      type: "info",
      key: "addProduct.scalingDeal",
    });
  }

  const fatalMessages = messages.filter((message) => message.type === "fatal");
  if (fatalMessages.length > 0 || (lineItem.contractErrorMessage && lineItem.contractErrorMessage.type === "fatal")) {
    messages = fatalMessages;
  }

  return messages;
}

function buildLineValidation(args: {
  lineItem: LineItem;
  descriptor: ProductDescriptor | null;
  saleActive: boolean;
  packSize: number;
  minOrderQuantity: number;
}): ProductLineValidationViewModel {
  const { lineItem, descriptor, saleActive, packSize, minOrderQuantity } = args;
  const isAddedFromQuote = lineItem.quoteLine !== "" && lineItem.quoteNumber !== "";
  const isMOQCheckEnabled = !!descriptor && isMinOrderQuantityRestrictionEnabled() && !saleActive && !isAddedFromQuote;
  const moqValidation = buildMOQHelperText({
    quantity: lineItem.quantity,
    packSize,
    minOrderQuantity,
    isAvailabilityLoaded: !!descriptor?.availability,
    isPackageSelected: !!lineItem.packCode,
    isOnSale: saleActive,
  });
  const isPackageSizeInvalid =
    isMOQCheckEnabled && moqValidation ? !moqValidation?.valid : !isValidQuantity(packSize, lineItem.quantity);
  const quantityHasError =
    isPackageSizeInvalid ||
    lineItem.messages.some((message) => message.key === PHASE_IN_BUY_ERROR_KEY) ||
    lineItem.messages.some((message) => message.key === CART_QTY_EXCEEDS_SALES_ERROR_KEY) ||
    lineItem.messages.some((message) => message.key === PHASEOUT2_EXCEED_STOCK_KEY);

  return {
    quantityHasError,
    isPackageSizeInvalid,
    MOQHelperText: moqValidation?.label ?? null,
    minQuantityValue: isMOQCheckEnabled ? getDefaultQuantity(packSize, minOrderQuantity) : packSize,
    isValid: lineItem.valid,
    disableEditing:
      lineItem.disableEdit ||
      lineItem.contractErrorMessage?.type === "fatal" ||
      lineItem.messages.some((message) => message.type === "fatal"),
  };
}

function buildLinePricing(args: {
  lineItem: LineItem;
  descriptor: ProductDescriptor | null;
  isBulkUpload: boolean;
  isPMA: boolean;
  isKIT: boolean;
  isOnSale: boolean;
  saleActive: boolean;
  salesStock: number;
}): ProductLinePricingViewModel {
  const { lineItem, descriptor, isBulkUpload, isPMA, isKIT, isOnSale, saleActive, salesStock } = args;
  const netPrice = lineItem.netPrice;
  const priceUnit = netPrice?.unit ?? 1;
  const tariffSurcharge = isTariffSurchargeEnabled() && (netPrice?.tariffSurcharge ?? false);
  const zeroPriceLabel = isPMA || isKIT ? null : { key: "product.priceOnRequest", fallback: "Price on request" };
  const salesPricing =
    saleActive && lineItem.actualNetPrice && lineItem.actualSalePrice
      ? processSalesPricing({
          netPrice: lineItem.actualNetPrice as NonNullable<LineItem["netPrice"]>,
          salesPrice: lineItem.actualSalePrice,
          saleActivated: saleActive,
        })
      : null;

  if (isBulkUpload || !netPrice) {
    return {
      netPrice,
      displayPrice: salesPricing?.price ?? null,
      displayOldPrice: salesPricing?.oldPrice ?? null,
      totalPrice: null,
      totalPriceFormatted: "...",
      surchargeValue: undefined,
      hasSurcharge: false,
      tariffSurcharge,
      priceUnit,
      isSpecialPrice: isDescriptorSpecialPrice(descriptor, lineItem.packCode),
      zeroPriceLabel,
      isOnSale,
      saleActive,
      salesStock,
      discountFormatted: salesPricing?.discountFormatted ?? null,
    };
  }

  const totalPrice = descriptor
    ? getTotalPriceForLineItem(lineItem, descriptor)
    : getTotalPrice(netPrice, lineItem.quantity);
  const hasSurcharge = (netPrice.surcharge ?? 0) > 0 && netPrice.value > 0;
  const displayPrice = salesPricing?.price ?? netPrice;
  const displayOldPrice = salesPricing?.oldPrice ?? null;

  return {
    netPrice,
    displayPrice,
    displayOldPrice,
    totalPrice,
    totalPriceFormatted: PriceUtils.text(totalPrice, "-"),
    surchargeValue: hasSurcharge ? getSurchargeValueFromTotalPrice(totalPrice) : undefined,
    hasSurcharge,
    tariffSurcharge,
    priceUnit,
    isSpecialPrice: isDescriptorSpecialPrice(descriptor, lineItem.packCode),
    zeroPriceLabel,
    isOnSale,
    saleActive,
    salesStock,
    discountFormatted: salesPricing?.discountFormatted ?? null,
  };
}

function buildLineFlags(args: {
  lineItem: LineItem;
  descriptor: ProductDescriptor | null;
  isBulkUpload: boolean;
}): ProductLineFlagsViewModel {
  const { lineItem, descriptor, isBulkUpload } = args;
  const quantityVisible = descriptor?.base.quantityVisible ?? true;
  const isOptime = descriptor?.base.isOptime ?? false;
  const isPMA = descriptor ? isProductPMA(descriptor) : false;
  const isKIT = !!descriptor && isKITProduct(descriptor.base);
  const canSeeComment = descriptor ? canUseCommentsForProduct(descriptor.base.marketType) : false;
  const inStock = descriptor ? productHasInStock(descriptor) : false;
  const isAddedFromQuote = lineItem.quoteLine !== "" && lineItem.quoteNumber !== "";
  const showBetterDeal = descriptor
    ? displayBetterDeal({ product: descriptor, packCode: lineItem.packCode }, lineItem.quantity)
    : false;

  return {
    inStock,
    isPMA,
    isKIT,
    quantityVisible,
    canSeeComment,
    isOptime,
    isBulkUpload,
    isAddedFromQuote,
    restrictedPackage: lineItem.restrictedPackage,
    showBetterDeal,
    webservicesDisabled: window.SCHA?.config?.disableAllWS ?? false,
  };
}

function buildLineFormatted(lineItem: LineItem, pricing: ProductLinePricingViewModel): ProductLineFormattedViewModel {
  return {
    quantity: lineItem.quantity,
    deliveryDate: lineItem.deliveryDate ? formatDate(lineItem.deliveryDate, DateFormatType.DATE) : "–",
    totalPrice: pricing.totalPriceFormatted,
    priceReference: lineItem.priceReference || "-",
    customerOrderNumber: lineItem.customerOrderNumber || "",
  };
}

function buildKitViewModel(
  mode: ProductTableMode,
  descriptor: ProductDescriptor | null,
  lineItem: LineItem,
  subLineItems: KitOptionalPart[],
  optionalPartDescriptors?: Record<number, ProductDescriptor>,
  phaseoutLocked?: boolean,
): ProductLineKitViewModel | null {
  if (!descriptor || !isKITProduct(descriptor.base)) {
    return null;
  }

  const kitProduct = descriptor as ProductDescriptor<KITProductData>;
  const includedParts = kitProduct.base.includedParts.map((part) => ({
    rowId: `${buildLineRowId(lineItem.id)}:included:${part.id}`,
    part,
    totalQuantity: part.quantity * lineItem.quantity,
  }));
  const includedPartsTotalQuantity = includedParts.reduce((sum, part) => sum + part.totalQuantity, 0);
  const optionalParts = kitProduct.base.optionalParts.reduce<ProductLineKitViewModel["optionalParts"]>(
    (accumulator, part) => {
      const optionalLineItem = subLineItems.find((subLineItem) => subLineItem.productCode === part.product.code);

      if (!optionalLineItem) {
        return accumulator;
      }

      const optDescriptor = optionalPartDescriptors?.[optionalLineItem.id] ?? null;
      const optDescriptorSelection: ProductDescriptorSelection = optDescriptor
        ? {
            descriptor: optDescriptor,
            strategy: "single-descriptor",
            priceReference: optionalLineItem.priceReference,
          }
        : {
            descriptor: null,
            strategy: "missing",
            priceReference: optionalLineItem.priceReference,
          };

      accumulator.push({
        rowId: `${buildLineRowId(optionalLineItem.id)}:optional:${part.id}`,
        part,
        line: buildSubLineSnapshot(optionalLineItem),
        sourceLineItem: optionalLineItem as unknown as LineItem,
        descriptor: optDescriptor,
        viewModel: optDescriptor
          ? buildProductLineViewModel({
              mode,
              lineItem: optionalLineItem as unknown as LineItem,
              descriptor: optDescriptor,
              descriptorSelection: optDescriptorSelection,
              subLineItems: [],
              phaseoutLocked,
            })
          : null,
      });

      return accumulator;
    },
    [],
  );
  const optionalPartsTotalQuantity = optionalParts.reduce((sum, item) => sum + item.line.quantity, 0);
  const optionalPartsTotalPrice = optionalParts.some((item) => !!item.line.netPrice)
    ? PriceUtils.volatileSum(...optionalParts.map((item) => getTotalPrice(item.line.netPrice, item.line.quantity)))
    : null;

  return {
    includedParts,
    includedPartsTotalQuantity,
    optionalParts,
    optionalPartsTotalQuantity,
    optionalPartsTotalPrice,
  };
}

export function buildProductLineViewModel({
  mode,
  lineItem,
  descriptor,
  descriptorSelection,
  subLineItems = [],
  allowPriceReferenceEditing = false,
  optionalPartDescriptors,
  phaseoutLocked = false,
}: BuildProductLineViewModelInput): ProductLineViewModel {
  const salesHelper = SalesHelper.of(descriptor);
  const isOnSale = salesHelper.isPackageOnSale(lineItem.packCode);
  const salesStock = salesHelper.forPackage(lineItem.packCode)?.stock ?? 0;
  const saleActive = lineItem.saleCampaignEnabled ?? false;
  const packSize = lineItem.packSize ?? 1;
  const minOrderQuantity = descriptor ? findMinOrderQuantity(descriptor, lineItem.packCode) : 0;
  const isBulkUpload = lineItem.source === PackageEntrySource.BULK;
  const flags = buildLineFlags({
    lineItem,
    descriptor,
    isBulkUpload,
  });
  const validation = buildLineValidation({
    lineItem,
    descriptor,
    saleActive,
    packSize,
    minOrderQuantity,
  });
  const pricing = buildLinePricing({
    lineItem,
    descriptor,
    isBulkUpload,
    isPMA: flags.isPMA,
    isKIT: flags.isKIT,
    isOnSale,
    saleActive,
    salesStock,
  });

  return {
    kind: "line",
    rowId: buildLineRowId(lineItem.id),
    lineItemId: lineItem.id,
    cartEntryId: lineItem.pk,
    productCode: lineItem.productCode,
    descriptor,
    descriptorSelection,
    sourceLineItem: lineItem,
    line: buildLineSnapshot(lineItem),
    subLines: subLineItems.map(buildSubLineSnapshot),
    pricing,
    validation,
    flags,
    formatted: buildLineFormatted(lineItem, pricing),
    kit: buildKitViewModel(mode, descriptor, lineItem, subLineItems, optionalPartDescriptors, phaseoutLocked),
    messages: buildLineMessages(lineItem, descriptor),
    phaseoutLocked,
  };
}
